import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch, STATUS_SWITCH } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Users,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Search,
  Eye,
  EyeOff,
  Percent,
  Store,
  DollarSign,
  Copy
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDistributors() {
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDistributor, setEditingDistributor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [issuingCode, setIssuingCode] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    commission_percent: 10,
    // Product Management v3 — per-channel commission.
    commission_percentages: { edisplay: 10, infinity: 15 },
    is_active: true
  });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
  });

  useEffect(() => {
    fetchDistributors();
  }, []);

  const fetchDistributors = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/distributors`, getAuthHeaders());
      setDistributors(response.data || []);
    } catch (error) {
      toast.error("Failed to load distributors");
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingDistributor(null);
    setFormData({
      name: "",
      email: "",
      password: "",
      commission_percent: 10,
      commission_percentages: { edisplay: 10, infinity: 15 },
      is_active: true
    });
    setShowPassword(false);
    setDialogOpen(true);
  };

  const openEditDialog = (distributor) => {
    setEditingDistributor(distributor);
    const cp = distributor.commission_percentages || {};
    setFormData({
      name: distributor.name,
      email: distributor.email,
      password: "",
      commission_percent: distributor.commission_percent,
      // Back-compat: fall back to legacy commission_percent for eDisplay.
      commission_percentages: {
        edisplay: cp.edisplay ?? distributor.commission_percent ?? 10,
        infinity: cp.infinity ?? distributor.commission_percent ?? 15,
      },
      is_active: distributor.is_active
    });
    setShowPassword(false);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email) {
      toast.error("Name and email are required");
      return;
    }
    
    if (!editingDistributor && !formData.password) {
      toast.error("Password is required for new distributors");
      return;
    }

    setSaving(true);
    try {
      if (editingDistributor) {
        await axios.put(
          `${API}/admin/distributors/${editingDistributor.distributor_id}`,
          formData,
          getAuthHeaders()
        );
        toast.success("Distributor updated!");
      } else {
        await axios.post(`${API}/admin/distributors`, formData, getAuthHeaders());
        toast.success("Distributor created!");
      }
      setDialogOpen(false);
      fetchDistributors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save distributor");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (distributor) => {
    if (!window.confirm(`Are you sure you want to delete ${distributor.name}?`)) {
      return;
    }

    try {
      await axios.delete(
        `${API}/admin/distributors/${distributor.distributor_id}`,
        getAuthHeaders()
      );
      toast.success("Distributor deleted!");
      fetchDistributors();
    } catch (error) {
      toast.error("Failed to delete distributor");
    }
  };

  const filteredDistributors = distributors.filter(d =>
    d.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (d.referral_code || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  /*
   * Referral code: never editable, but issuable.
   *
   * Funeral homes quote it to sign up, so once it is on a card or in an email it
   * must not change underneath them — the update endpoint strips the field, and
   * there is no text input for it anywhere. What was missing is the other half:
   * most distributors pre-date the feature and hold no code at all, and nothing
   * in the admin panel could give them one. Issuing is therefore a separate
   * action on its own endpoint, offered only when there is no code to break.
   */
  const copyReferralCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Copied ${code}`);
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  const handleIssueReferralCode = async () => {
    if (!editingDistributor) return;
    setIssuingCode(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/distributors/${editingDistributor.distributor_id}/referral-code`,
        {},
        getAuthHeaders()
      );
      // Keep the open dialog in step with what was just minted, so the code is
      // there to copy without closing and reopening.
      setEditingDistributor(prev => ({ ...prev, referral_code: data.referral_code }));
      toast.success(data.message || `Referral code ${data.referral_code} issued`);
      fetchDistributors();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to issue referral code");
    } finally {
      setIssuingCode(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-distributors">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Distributors
          </h1>
          <p className="text-slate-500">Manage distributors and their commission rates</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-amber-600 hover:bg-amber-700 gap-2">
          <Plus className="w-4 h-4" />
          Add Distributor
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search distributors..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{distributors.length}</p>
              <p className="text-sm text-slate-500">Total Distributors</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Store className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {distributors.reduce((acc, d) => acc + (d.assigned_resellers_count || 0), 0)}
              </p>
              <p className="text-sm text-slate-500">Assigned Resellers</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                ${distributors.reduce((acc, d) => acc + (d.total_earnings || 0), 0).toFixed(2)}
              </p>
              <p className="text-sm text-slate-500">Total Commissions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {filteredDistributors.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No distributors found</p>
            <Button onClick={openCreateDialog} variant="link" className="text-amber-600">
              Add your first distributor
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Referral code</TableHead>
                <TableHead className="text-center">Commission %</TableHead>
                <TableHead className="text-center">Resellers</TableHead>
                <TableHead className="text-center">Total Earnings</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDistributors.map((distributor) => (
                <TableRow key={distributor.distributor_id}>
                  <TableCell className="font-medium">{distributor.name}</TableCell>
                  <TableCell>{distributor.email}</TableCell>
                  <TableCell>
                    {distributor.referral_code ? (
                      <div className="flex items-center gap-1">
                        <code className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">
                          {distributor.referral_code}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          title="Copy referral code"
                          onClick={() => copyReferralCode(distributor.referral_code)}
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-red-600" title="Funeral homes cannot sign up under this distributor until a code is issued">
                        None issued
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="secondary" className="gap-1">
                      <Percent className="w-3 h-3" />
                      {distributor.commission_percent}%
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    {distributor.assigned_resellers_count || 0}
                  </TableCell>
                  <TableCell className="text-center font-medium text-green-600">
                    ${(distributor.total_earnings || 0).toFixed(2)}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={distributor.is_active ? "default" : "secondary"}>
                      {distributor.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(distributor)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(distributor)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingDistributor ? "Edit Distributor" : "Add Distributor"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Distributor name"
                data-testid="distributor-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
                data-testid="distributor-email"
              />
            </div>

            {/*
              * Shown on edit only — on create the code does not exist yet, it is
              * minted when the record is saved. Never an input: funeral homes sign
              * up by quoting this, so it must not change once it has been shared.
              */}
            {editingDistributor && (
              <div className="space-y-2">
                <Label>Referral code</Label>
                <div className="flex items-center gap-2">
                  <code className="font-mono text-sm bg-slate-100 px-3 py-2 rounded flex-1">
                    {editingDistributor.referral_code || "None issued"}
                  </code>
                  {editingDistributor.referral_code ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => copyReferralCode(editingDistributor.referral_code)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleIssueReferralCode}
                      disabled={issuingCode}
                      data-testid="issue-referral-code"
                    >
                      {issuingCode ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Generate code"
                      )}
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Funeral homes enter this code to sign up and be linked to this distributor.
                  Deactivating the distributor stops the code working for new signups.
                  {!editingDistributor.referral_code && " Generating one emails it to the distributor."}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>{editingDistributor ? "New Password (leave blank to keep)" : "Password"}</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  data-testid="distributor-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Commission Percentage (per channel)</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <span className="text-xs text-slate-500">e-Displayroom</span>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.commission_percentages.edisplay}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value) || 0;
                        setFormData({
                          ...formData,
                          // keep legacy commission_percent aligned with eDisplay
                          commission_percent: v,
                          commission_percentages: { ...formData.commission_percentages, edisplay: v },
                        });
                      }}
                      className="w-24"
                      data-testid="distributor-commission"
                    />
                    <span className="text-slate-500">%</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-blue-600">Infinity Touch</span>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.commission_percentages.infinity}
                      onChange={(e) => setFormData({
                        ...formData,
                        commission_percentages: { ...formData.commission_percentages, infinity: parseFloat(e.target.value) || 0 },
                      })}
                      className="w-24"
                      data-testid="distributor-commission-infinity"
                    />
                    <span className="text-slate-500">%</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                className={STATUS_SWITCH}
                data-testid="distributor-active"
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingDistributor ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
